import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Paper,
  Stack,
  Box,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  HourglassEmpty,
  PlayArrow,
  Schedule,
  Refresh,
  Settings,
} from '@mui/icons-material';
import { Add } from '@mui/icons-material';
import type { SetupTask, SetupTaskStatus, SetupTaskOptions } from '@finvari/deliver';
import { formatDate, getDuration, getCloudBuildUrl } from '../utils/deploymentUtils';
import { retrySetupTask, createSetupTaskForRollout, failSetupTask } from '../firebase';
import { Cancel } from '@mui/icons-material';
import DeploymentOptionsDialog from './DeploymentOptionsDialog';
import { useConfig } from '../contexts/ConfigContext';

function getSetupTaskStatusColor(status: SetupTaskStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
  switch (status) {
    case 'success': return 'success';
    case 'failed': return 'error';
    case 'in_progress': return 'info';
    case 'pending': return 'warning';
    case 'awaiting_deploy': return 'default';
    default: return 'default';
  }
}

function getSetupTaskStatusIcon(status: SetupTaskStatus) {
  switch (status) {
    case 'success': return <CheckCircle fontSize="small" />;
    case 'failed': return <Error fontSize="small" />;
    case 'in_progress': return <PlayArrow fontSize="small" />;
    case 'pending': return <HourglassEmpty fontSize="small" />;
    case 'awaiting_deploy': return <Schedule fontSize="small" />;
    default: return <HourglassEmpty fontSize="small" />;
  }
}

interface SetupTaskCardProps {
  setupTasks: SetupTask[];
  hasActiveOperations?: boolean;
  rolloutId?: string;
  environmentId?: string;
  // When set, actions that run a build (prepare / retry) are disabled — e.g. a pooled environment
  // not leased to the current user. Shown as the button tooltip. Mark-as-failed stays available
  // since it only reconciles a stuck record and triggers nothing on the environment.
  lockReason?: string;
}

export default function SetupTaskCard({ setupTasks, hasActiveOperations = false, rolloutId, environmentId, lockReason }: SetupTaskCardProps) {
  const { config } = useConfig();
  const [retrying, setRetrying] = useState<string | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<SetupTask | null>(null);
  const [creating, setCreating] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [failing, setFailing] = useState<string | null>(null);

  const handleCreateSetupTask = async (options: SetupTaskOptions) => {
    if (!rolloutId) return;
    setCreateDialogOpen(false);
    setCreating(true);
    setRetryError(null);
    try {
      await createSetupTaskForRollout(rolloutId, options);
    } catch (error: any) {
      console.error('Error creating setup task:', error);
      setRetryError(error.message || 'Failed to create setup task');
    } finally {
      setCreating(false);
    }
  };

  if (setupTasks.length === 0) {
    if (!rolloutId || !environmentId) return null;
    return (
      <>
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Setup Tasks"
            subheader="No setup tasks for this rollout"
          />
          <CardContent>
            {retryError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRetryError(null)}>
                {retryError}
              </Alert>
            )}
            <Button
              variant="outlined"
              startIcon={creating ? <CircularProgress size={16} /> : <Add />}
              onClick={() => setCreateDialogOpen(true)}
              disabled={creating || hasActiveOperations || !!lockReason}
              title={lockReason ?? (hasActiveOperations ? 'Wait for active operations to complete' : undefined)}
            >
              Prepare Environment
            </Button>
          </CardContent>
        </Card>
        <DeploymentOptionsDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          environmentId={environmentId}
          mode="retrySetup"
          onRetrySetup={handleCreateSetupTask}
        />
      </>
    );
  }

  // Helper to get group name or fall back to displaying items
  const getProgramsDisplay = (options: SetupTaskOptions): { label: string; title?: string } => {
    if (options.selectedProgramGroupId) {
      const group = config?.programGroups?.[options.selectedProgramGroupId];
      if (group) {
        return { label: `Programs: ${group.name}`, title: options.loadPrograms.join(', ') };
      }
    }
    return { label: `Programs: ${options.loadPrograms.join(', ')}` };
  };

  const getAdminsDisplay = (options: SetupTaskOptions): { label: string; title?: string } => {
    if (options.selectedUserGroupId) {
      const group = config?.userGroups?.[options.selectedUserGroupId];
      if (group) {
        return { label: `Admins: ${group.name}`, title: options.adminEmails.join(', ') };
      }
    }
    return { label: `Admins: ${options.adminEmails.length}`, title: options.adminEmails.join(', ') };
  };

  const handleRetry = async (task: SetupTask) => {
    setRetrying(task.taskId);
    setRetryError(null);
    try {
      await retrySetupTask(task.rolloutId, task.taskId);
    } catch (error: any) {
      console.error('Error retrying setup task:', error);
      setRetryError(error.message || 'Failed to retry setup task');
    } finally {
      setRetrying(null);
    }
  };

  const handleRetryWithNewSettings = (task: SetupTask) => {
    setSelectedTask(task);
    setDialogOpen(true);
  };

  const handleDialogConfirm = async (options: SetupTaskOptions) => {
    if (!selectedTask) return;
    setDialogOpen(false);
    setRetrying(selectedTask.taskId);
    setRetryError(null);
    try {
      await retrySetupTask(selectedTask.rolloutId, selectedTask.taskId, options);
    } catch (error: any) {
      console.error('Error retrying setup task with new options:', error);
      setRetryError(error.message || 'Failed to retry setup task');
    } finally {
      setRetrying(null);
      setSelectedTask(null);
    }
  };

  const canMarkFailed = (task: SetupTask): boolean => {
    if (task.status === 'success' || task.status === 'failed') return false;
    const ageMs = Date.now() - new Date(task.createdAt).getTime();
    if (!task.buildId) return ageMs >= 30 * 60 * 1000;
    if (!task.finishedAt) return ageMs >= 60 * 60 * 1000;
    return false;
  };

  const handleMarkFailed = async (task: SetupTask) => {
    setFailing(task.taskId);
    setRetryError(null);
    try {
      await failSetupTask(task.rolloutId, task.taskId);
    } catch (error: any) {
      console.error('Error marking setup task as failed:', error);
      setRetryError(error.message || 'Failed to mark setup task as failed');
    } finally {
      setFailing(null);
    }
  };

  return (
    <>
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Setup Tasks"
          subheader="Post-deployment environment preparation"
        />
        <CardContent>
          {retryError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRetryError(null)}>
              {retryError}
            </Alert>
          )}
          {setupTasks.map(task => {
            const canRetry = task.status === 'failed' || task.status === 'success';
            const isRetrying = retrying === task.taskId;

            return (
              <Paper key={task.taskId} elevation={1} sx={{ p: 2, mb: 2 }}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {task.taskType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        icon={getSetupTaskStatusIcon(task.status)}
                        label={task.status.replace('_', ' ')}
                        color={getSetupTaskStatusColor(task.status)}
                        size="small"
                        variant="outlined"
                      />
                      {task.buildId && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => window.open(getCloudBuildUrl(task.buildId!, task.environment), '_blank')}
                        >
                          View Build
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {/* Options */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {task.options.wipeData && (
                      <Chip label="Wipe Data" size="small" variant="outlined" color="warning" />
                    )}
                    {task.options.loadFullBackup && (
                      <Chip label="Load Backup" size="small" variant="outlined" />
                    )}
                    {task.options.loadPrograms.length > 0 && (() => {
                      const { label, title } = getProgramsDisplay(task.options);
                      return (
                        <Chip
                          label={label}
                          size="small"
                          variant="outlined"
                          title={title}
                        />
                      );
                    })()}
                    {task.options.adminEmails.length > 0 && (() => {
                      const { label, title } = getAdminsDisplay(task.options);
                      return (
                        <Chip
                          label={label}
                          size="small"
                          variant="outlined"
                          title={title}
                        />
                      );
                    })()}
                  </Box>

                  {/* Timing */}
                  <Typography variant="caption" color="text.secondary">
                    Created: {formatDate(task.createdAt)}
                    {task.startedAt && <> • Started: {formatDate(task.startedAt)}</>}
                    {task.finishedAt && <> • Finished: {formatDate(task.finishedAt)}</>}
                    {task.startedAt && task.finishedAt && (
                      <> • Duration: {getDuration(task.startedAt, task.finishedAt)}</>
                    )}
                  </Typography>

                  {/* Error message */}
                  {task.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {task.error}
                    </Alert>
                  )}

                  {/* Action buttons */}
                  {(canRetry || canMarkFailed(task)) && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      {canRetry && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={isRetrying ? <CircularProgress size={16} /> : <Refresh />}
                            onClick={() => handleRetry(task)}
                            disabled={isRetrying || hasActiveOperations || !!lockReason}
                            title={lockReason ?? (hasActiveOperations ? 'Wait for active operations to complete' : undefined)}
                          >
                            Retry
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Settings />}
                            onClick={() => handleRetryWithNewSettings(task)}
                            disabled={isRetrying || hasActiveOperations || !!lockReason}
                            title={lockReason ?? (hasActiveOperations ? 'Wait for active operations to complete' : undefined)}
                          >
                            Retry with New Settings
                          </Button>
                        </>
                      )}
                      {canMarkFailed(task) && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={failing === task.taskId ? <CircularProgress size={16} /> : <Cancel />}
                          onClick={() => handleMarkFailed(task)}
                          disabled={failing === task.taskId}
                          title={!task.buildId
                            ? 'Task has not started after 30+ minutes'
                            : 'Task has not finished after 60+ minutes'}
                        >
                          Mark as Failed
                        </Button>
                      )}
                    </Box>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </CardContent>
      </Card>

      {selectedTask && (
        <DeploymentOptionsDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setSelectedTask(null);
          }}
          environmentId={selectedTask.environment}
          mode="retrySetup"
          initialOptions={selectedTask.options}
          onRetrySetup={handleDialogConfirm}
        />
      )}
    </>
  );
}
